import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { ArrowRight } from 'lucide-react';
import { PhoneIcon, EnvelopeIcon, MapPinIcon } from '@/components/icons/ContactIcons';
import { IconCircle } from '@/components/ui/IconCircle';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { DarkField, DarkInput, DarkCheckbox } from '@/components/forms/DarkFormControls';
import { contactFormSchema, type ContactFormValues } from '@/lib/validation';
import { sendContactEmail } from '@/lib/emailjs';
import { CONTACT } from '@/lib/constants';

import { buttonClasses } from '@/components/ui/buttonStyles';
import { cn } from '@/lib/cn';

interface ContactSectionProps {
  className?: string;
  /** Optional background image (defaults to the home subhero tree pattern). Pass `null` to disable. */
  backgroundImage?: string | null;
}

const SUBHERO_BG = '/assets/images/home/subhero-bg.png';

/**
 * Home "Pronto para virar essa página?" band — purple bg with tree pattern,
 * contact info on left, dark-themed inline contact form on right.
 * Currently only used on Home (EEM-023); restyled to match the Figma.
 */
export function ContactSection({ className, backgroundImage }: ContactSectionProps) {
  const bg = backgroundImage === null ? undefined : backgroundImage ?? SUBHERO_BG;

  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      nome: '',
      email: '',
      telefone: '',
      mensagem: '',
      comunicacoes: false,
      privacidade: false,
    },
  });

  const submit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await sendContactEmail(values);
      setSubmitted(true);
      reset();
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Não foi possível enviar sua mensagem. Tente novamente.'
      );
    }
  });

  return (
    <section
      id="contact"
      className={cn(
        'relative isolate bg-brand-purple text-white py-14 md:py-28 overflow-hidden scroll-mt-24',
        className
      )}
    >
      {bg && (
        <>
          <img
            src={bg}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-contain lg:object-cover -z-10 opacity-60"
            loading="lazy"
            decoding="async"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-brand-purple/70"
          />
        </>
      )}

      <div className="container-page grid gap-0 md:gap-14 lg:grid-cols-2 items-start">
        {/* LEFT — heading + highlighted subtitle + smaller intro + contact list */}
        <div className="space-y-2 md:space-y-8">
          <h2 className="heading-display flex-row flex-wrap text-4xl md:text-6xl lg:text-7xl leading-[1.05]">
            <span className="font-extrabold text-brand-yellow">Pronto</span>
            <span className="text-white">&nbsp;para</span>
            <br className="w-full" />
            <span className="text-white">virar essa página?</span>
          </h2>

          <p className="text-lg md:text-3xl text-white leading-snug max-w-lg">
            Estamos aqui para ajudar você a
            <span className="inline-block rounded-3xl bg-brand-yellow px-4 py-0 md:py-1 text-brand-purple font-bold">
              encontrar o equilíbrio.
            </span>
          </p>

          <p className="text-xs md:text-sm text-white/70 max-w-md">
            Preencha o formulário para receber um contato seguro ou inicie uma
            conversa agora mesmo pelo{' '}
            <a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white font-semibold underline hover:text-brand-yellow transition-colors"
            >
              WhatsApp
            </a>
          </p>

          <ul className="hidden lg:flex flex-col gap-5 pt-2">
            <li className="flex items-start gap-4">
              <IconCircle tone="purple" size="lg">
                <PhoneIcon className="text-brand-yellow" />
              </IconCircle>
              <div className="text-white pt-2">
                <p className="text-sm text-white/70">Telefone</p>
                <p className="font-semibold">{CONTACT.phone}</p>
              </div>
            </li>

            <li className="flex items-start gap-4">
              <IconCircle tone="purple" size="lg">
                <EnvelopeIcon className="text-brand-yellow pb-2" />
              </IconCircle>
              <div className="text-white pt-1">
                <p className="text-sm text-white/70">E-mail</p>
                <p className="font-semibold">{CONTACT.email}</p>
              </div>
            </li>

            <li className="flex items-start gap-4">
              <IconCircle tone="purple" size="lg">
                <MapPinIcon className="text-brand-yellow" />
              </IconCircle>
              <div className="text-white">
                <p className="text-sm text-white/70">Endereço</p>
                <p className="font-semibold">{CONTACT.address.street},</p>
                <p className="font-semibold">
                  4º andar, {CONTACT.address.city} - {CONTACT.address.state}.
                </p>
              </div>
            </li>
          </ul>
        </div>

        {/* RIGHT — dark inline form (no card) */}
        {submitted ? (
          <div
            role="status"
            className="rounded-card-lg border border-white/15 bg-white/5 p-8 text-center space-y-3"
          >
            <h3 className="text-xl font-semibold">Recebemos sua mensagem.</h3>
            <p className="text-sm text-white/75">
              Em breve nossa equipe entrará em contato.
            </p>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="text-sm font-medium text-brand-yellow hover:underline"
              style={{ cursor: 'pointer' }}
            >
              Enviar outra mensagem
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            noValidate
            aria-label="Formulário de contato"
            className="flex flex-col gap-6"
          >
            <DarkField
              id="home-contact-nome"
              label="Seu nome"
              required
              error={errors.nome?.message}
            >
              <DarkInput
                id="home-contact-nome"
                autoComplete="name"
                placeholder="Digite seu nome completo"
                hasError={Boolean(errors.nome)}
                {...register('nome')}
              />
            </DarkField>

            <DarkField
              id="home-contact-email"
              label="Seu e-mail"
              required
              error={errors.email?.message}
            >
              <DarkInput
                id="home-contact-email"
                type="email"
                autoComplete="email"
                placeholder="seuemail@exemplo.com"
                hasError={Boolean(errors.email)}
                {...register('email')}
              />
            </DarkField>

            <DarkField
              id="home-contact-telefone"
              label="Telefone"
              error={errors.telefone?.message}
            >
              <DarkInput
                id="home-contact-telefone"
                type="tel"
                autoComplete="tel"
                placeholder="(00) 00000-0000"
                hasError={Boolean(errors.telefone)}
                {...register('telefone')}
              />
            </DarkField>

            <div className="flex flex-col gap-3 pt-1">
              <DarkCheckbox
                id="home-contact-comunicacoes"
                label="Eu concordo em receber comunicações"
                {...register('comunicacoes')}
              />
              <DarkCheckbox
                id="home-contact-privacidade"
                hasError={Boolean(errors.privacidade)}
                label={
                  <>
                    Ao informar meus dados eu concordo com a{' '}
                    <strong>Política de Privacidade/ Termos de Uso</strong>
                  </>
                }
                {...register('privacidade')}
              />
              {errors.privacidade && (
                <p role="alert" className="text-xs text-red-300">
                  {errors.privacidade.message}
                </p>
              )}
            </div>

            {submitError && (
              <p role="alert" className="text-sm text-red-300">
                {submitError}
              </p>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className={buttonClasses(
                  'primary-yellow',
                  'lg',
                  'cursor-pointer py-1! md:py-3.5!'
                )}
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" tone="purple" />
                    Enviando…
                  </>
                ) : (
                  <>
                    Negocie com a gente
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

